import { Color, TypographyColor } from "../components/typography"
import { ContainerBackgroundColor, ContainerVariant, containerBackgroundOptions } from "../components/layout"
import { Repeater, types } from "react-bricks/frontend"
import Typography, { TypographyElement } from "../Typography"

import React from "react"
import { RoundedBackgroundContainer } from "../components/layout"
import StarsSVG from "../components/icon/stars"
import { TextLinkBrickProps } from "../text/TextLink"
import { TextLinkSize } from "../components/text"
import { useCheckForEmptyText } from "../../hooks/use-check-for-empty-text"
import { useTranslation } from "react-i18next"

export interface StarsTextProps {
    background: { color: ContainerBackgroundColor } | ContainerBackgroundColor
    color?: TypographyColor
    text: TypographyElement[]
    variant?: ContainerVariant
    index?: number
    trustedShops?: boolean
}

const StarsText: types.Brick<StarsTextProps> = ({ background, color, variant, text, index, trustedShops }) => {
    const { t } = useTranslation()
    const emptyText = useCheckForEmptyText(text)

    const bgColor = typeof background === "object" ? background.color : background

    return (
        <li key={index}>
            <RoundedBackgroundContainer variant={variant} background={bgColor} className="flex h-full flex-col">
                <div className="flex items-center gap-3 sm:gap-5">
                    <StarsSVG />
                    {trustedShops && (
                        <>
                            <div className="flex items-center gap-1">
                                <div className="w-[18px]">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
                                        <g id="Gruppe_20820" data-name="Gruppe 20820" transform="translate(0 0)">
                                            <g id="Gruppe_22759" data-name="Gruppe 22759">
                                                <path
                                                    id="Pfad_11265"
                                                    data-name="Pfad 11265"
                                                    d="M9,0,7.485,1.514,5.5.707,4.717,2.635H2.636V4.777L.662,5.61l.809,1.917L0,9l1.515,1.515L.708,12.5l1.928.784v2.081H4.778l.833,1.974,1.917-.809L9,18l1.515-1.515,1.985.807.784-1.928h2.081V13.221l1.974-.833-.809-1.917L18,9,16.485,7.484,17.292,5.5l-1.928-.784V2.635H13.222L12.389.661l-1.917.809Z"
                                                    transform="translate(0 0.001)"
                                                    fill="#00497a"
                                                />
                                                <path
                                                    id="Pfad_11349"
                                                    data-name="Pfad 11349"
                                                    d="M8.407-7.02a.71.71,0,0,0-1,0L3.36-2.976,1.738-4.6a.7.7,0,0,0-.5-.205.71.71,0,0,0-.5.208.709.709,0,0,0,0,1l2.12,2.119h0a.71.71,0,0,0,1,0L8.406-6.016a.71.71,0,0,0,0-1Z"
                                                    transform="translate(4.246 13.342)"
                                                    fill="#fff"
                                                />
                                            </g>
                                        </g>
                                    </svg>
                                </div>
                                <span className="text-sm font-semibold text-blue">
                                    {t("stars_text_verified")}
                                </span>
                            </div>
                            <div className="ml-auto w-[24px]">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36">
                                    <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M6.481 17.89c0 6.238 5.057 11.294 11.295 11.294 6.237 0 11.294-5.056 11.294-11.294S24.013 6.596 17.776 6.596c-6.238 0-11.295 5.056-11.295 11.294ZM0 17.947C0 8.047 8.025.024 17.924.024c9.9 0 17.924 8.024 17.924 17.923 0 9.9-8.025 17.925-17.924 17.925C8.024 35.872 0 27.847 0 17.947Zm20.192-6.69c.857 0 1.322.644 1.322 1.465 0 2.822-3.322 4.785-5.714 5.499.5-1.964 1.821-6.963 4.392-6.963Zm-3.963 14.784c3.07 0 5.356-2.215 6.999-4.642l-.894-.679c-1.178 1.428-2.784 3.357-4.785 3.357-1.535 0-2.284-1.287-2.284-2.678 0-.679.107-1.357.214-2.035l.498-.125c3.236-.806 8.928-2.223 8.928-6.052 0-2.25-2.499-3-4.356-3-5.535 0-9.462 5.213-9.462 10.533 0 3.285 1.892 5.32 5.142 5.32Z"
                                        fill="#000"
                                    />
                                </svg>
                            </div>
                        </>
                    )}
                </div>
                {!emptyText && (
                    <Color color={bgColor === ContainerBackgroundColor.Transparent && color ? color : undefined}>
                        <Typography block="copytext-large" allowList={["small", "sup", "link", "bold"]} />
                    </Color>
                )}
                <Repeater propName="repeater" itemProps={{ size: TextLinkSize.Small } as TextLinkBrickProps} />
            </RoundedBackgroundContainer>
        </li>
    )
}

StarsText.schema = {
    name: "StarsText",
    label: "Stars Text",
    hideFromAddMenu: true,
    repeaterItems: [
        {
            name: "repeater",
            items: [
                {
                    type: "TextLink",
                    label: "Text Link",
                    max: 1,
                },
            ],
        },
    ],
    sideEditProps: [
        {
            name: "background",
            label: "Background",
            type: types.SideEditPropType.Select,
            selectOptions: {
                options: containerBackgroundOptions,
                display: types.OptionsDisplay.Color,
            },
        },
        {
            name: "trustedShops",
            label: "Trusted Shops",
            type: types.SideEditPropType.Boolean,
        },
    ],
    getDefaultProps: (): StarsTextProps => ({
        background: { color: ContainerBackgroundColor.Grey },
        trustedShops: false,
        text: [
            {
                type: "paragraph",
                children: [
                    {
                        text: "Lorem ipsum dolor sit amet, consectetur adipiscing elit. Nunc placerat sagittis faucibus.",
                    },
                ],
            },
        ],
    }),
}

export default StarsText
